import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useErrors } from '../../../../hooks/errors/use-errors';
import { useNative } from '../../../../hooks/native/use-native';
import { useHttpClient } from '../../../../utils/clients/rest-client';
import { toastSuccess } from '../../../../utils/toast';
import { useUpdateChannel } from '../../hooks/channel/use-update-channel';
import {
  GateCalibrateData,
  GateCalibrateRequest,
  GateSetAlertConfigData,
  GateSetAlertConfigError,
  GateSetAlertConfigRequest,
  GateSetDailyModeData,
  GateSetDailyModeError,
  GateSetDailyModeStateRequest,
  GateSetDirectionData,
  GateSetDirectionRequest,
  GateSetNegativeOutputData,
  GateSetNegativeOutputError,
  GateSetNegativeOutputRequest,
  GateSetPartialControlModeData,
  GateSetPartialControlModeError,
  GateSetPartialControlModeStateRequest,
  GateSetPartialSbsSignalTimeData,
  GateSetPartialSbsSignalTimeError,
  GateSetPartialSbsSignalTimeRequest,
  GateSetPositionData,
  GateSetPositionRequest,
  GateSetSbsSignalTimeData,
  GateSetSbsSignalTimeError,
  GateSetSbsSignalTimeRequest,
  GateSetTiltTimeData,
  GateSetTiltTimeError,
  GateSetTiltTimeRequest,
  GateStepByStepData,
  GateStepByStepRequest,
  GateTiltData,
  GateTiltRequest,
} from '../../types/openapi/openapi-types';

export const useGateSetPosition = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<GateSetPositionData>, AxiosError<void>, GateSetPositionRequest>(
    ['gate-setPosition'],
    async (body) => {
      fireVibration();
      return await httpClient.gate.gateSetPosition(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({ status: data.publishStatus, deviceId: body.deviceId });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useGateSetDirection = () => {
  const httpClient = useHttpClient();
  const { resetCalibrateState } = useUpdateChannel();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<GateSetDirectionData>, AxiosError<void>, GateSetDirectionRequest>(
    ['gate-setDirection'],
    async (body) => {
      fireVibration();
      return await httpClient.gate.gateSetDirection(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({
          status: data.publishStatus,
          deviceId: body.deviceId,
          onSuccess: () => {
            resetCalibrateState(body.channelId, data);
          },
        });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useGateStepByStep = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<GateStepByStepData>, AxiosError<void>, GateStepByStepRequest>(
    ['gate-stepByStep'],
    async (body) => {
      fireVibration();
      return await httpClient.gate.gateStepByStep(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({ status: data.publishStatus, deviceId: body.deviceId });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useGateTilt = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<GateTiltData>, AxiosError<void>, GateTiltRequest>(
    ['gate-tilt'],
    async (body) => {
      fireVibration();
      return await httpClient.gate.gateTilt(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({ status: data.publishStatus, deviceId: body.deviceId });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useGateCalibrate = () => {
  const { t: td } = useTranslation('channel-settings');
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();

  return useMutation<AxiosResponse<GateCalibrateData>, AxiosError<void>, GateCalibrateRequest>(
    ['gate-calibrate'],
    async (body) => await httpClient.gate.gateCalibrate(body),
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({
          status: data.publishStatus,
          deviceId: body.deviceId,
          onSuccess: () => {
            toastSuccess({ content: td('toast.calibrationStartSent') });
          },
        });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useGateSetSbsSignalTime = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<
    AxiosResponse<GateSetSbsSignalTimeData>,
    AxiosError<GateSetSbsSignalTimeError>,
    GateSetSbsSignalTimeRequest
  >(
    ['gate-setSbsSignalTime'],
    async (body) => {
      return await httpClient.gate.gateSetSbsSignalTime(body);
    },
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useGateSetPartialSignalTime = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<
    AxiosResponse<GateSetPartialSbsSignalTimeData>,
    AxiosError<GateSetPartialSbsSignalTimeError>,
    GateSetPartialSbsSignalTimeRequest
  >(
    ['gate-setPartialSbsSignalTime'],
    async (body) => {
      return await httpClient.gate.gateSetPartialSbsSignalTime(body);
    },
    {
      onError: (error: AxiosError) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useGateSetTiltTime = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<AxiosResponse<GateSetTiltTimeData>, AxiosError<GateSetTiltTimeError>, GateSetTiltTimeRequest>(
    ['gate-setTiltTime'],
    async (body) => {
      return await httpClient.gate.gateSetTiltTime(body);
    },
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useGateSetAlertConfig = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<
    AxiosResponse<GateSetAlertConfigData>,
    AxiosError<GateSetAlertConfigError>,
    GateSetAlertConfigRequest
  >(
    ['gate-setAlertConfig'],
    async (body) => {
      return await httpClient.gate.gateSetAlertConfig(body);
    },
    {
      onError: (error: AxiosError) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useSetPartialControlMode = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<
    AxiosResponse<GateSetPartialControlModeData>,
    AxiosError<GateSetPartialControlModeError>,
    GateSetPartialControlModeStateRequest
  >(
    ['gate-partialControlMode'],
    async (body) => {
      fireVibration();
      return await httpClient.gate.gateSetPartialControlMode(body);
    },
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useSetDailyMode = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<
    AxiosResponse<GateSetDailyModeData>,
    AxiosError<GateSetDailyModeError>,
    GateSetDailyModeStateRequest
  >(
    ['gate-dailyMode'],
    async (body) => {
      fireVibration();
      return await httpClient.gate.gateSetDailyMode(body);
    },
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useSetNegativeOutput = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<
    AxiosResponse<GateSetNegativeOutputData>,
    AxiosError<GateSetNegativeOutputError>,
    GateSetNegativeOutputRequest
  >(
    ['gate-setNegativeOutput'],
    async (body) => {
      fireVibration();
      return await httpClient.gate.gateSetNegativeOutput(body);
    },
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};
